// src/lib/status-breakdown.ts
//
// ★★★ WAVE 8 (Task 2, review fix — Important 1). THE FOUR BREAKDOWN TILES MUST
// PARTITION THE STATUS DOMAIN, AND NOTHING PROVED THAT THEY DID.
//
// `DashboardPage` prints *Bills in scope* — the SUM of every group
// `GET /api/bills/status-counts` returned — and then four tiles beneath it:
// level 1, level 2, approved, rejected. The four were computed inline in the
// page, from `pendingDecision(status)` and two string equalities, and every one
// of those classifiers answers `null`/`false` for a status it does not know.
// `countWhere` then skipped the group in silence.
//
// ⚠ **So a SIXTH status added by a migration would land in `total` and in no
// tile**, and the four would stop summing to the figure above them — the exact
// defect Task 2 exists to remove, re-entering through the one door the fix left
// open. Nothing could see it: `pendingDecision`'s parameter is `string`, so
// `tsc` had no opinion; the summation assertion ran against a hand-written
// five-status fixture rather than the status domain; and the footnotes that used
// to disclose partiality had just been (correctly) deleted, so the screen would
// have looked uniformly authoritative while being arithmetically wrong.
//
// ══ WHAT THIS FILE IS, AND WHY IT IS NOT IN THE PAGE ══
//
// The same move `lib/queue.ts` made for the approval queue's three-way split:
// the classification is pulled out of the component so it can be ASSERTED
// without a DOM, and so the assertion is about the definition rather than about
// one rendered fixture. `status-breakdown.test.ts` is the guard, and it holds
// the whole chain in one place:
//
//   infra/local/sql/003_domain.sql's `status` CHECK
//        -> `BILL_STATUSES` (lib/types.ts)
//        -> exactly one of `BREAKDOWN_TILES` claims each member
//
// A status added to the database without a tile fails that suite. A status added
// to `BILL_STATUSES` without a tile fails it. A status added to a tile that
// another tile already claims fails it too — "exactly one", not "at least one",
// because two tiles claiming `APPROVED` would make the four OVER-count and sum
// past the total, which is the same lie in the other direction.
//
// ══ AND THE HALF NO CHECKED-IN GUARD CAN SEE ══
//
// That chain compares two files in this repository to each other. It cannot see
// a DATABASE that is ahead of the build reading it — a migration applied to a
// deployed environment whose officer-web bundle predates it. In that state every
// test is green and the screen is still wrong, so the classification ALSO
// reports what it could not place: `unclassified` counts those rows and
// `unclassifiedStatuses` names them, and the dashboard renders them as their own
// visible figure rather than absorbing them. A partition guard fails in CI; an
// unclassified figure fails on screen, in front of the officer, where the
// database is the authority. Both, because they cover different states.
import { pendingDecision } from './authority'

/**
 * One of the four breakdown tiles, and the statuses it claims.
 *
 * ⚠ `claims` takes a `string`, not a `BillStatus`, on purpose: the input is a
 * key of the endpoint's `byStatus` map, which is whatever `app.bill.status`
 * actually holds in the database being read — not whatever this build's union
 * says it may hold. Narrowing the parameter would move the problem from a
 * visible miscount to an invisible one.
 */
export interface BreakdownTile {
  readonly key: 'awaitingL1' | 'awaitingL2' | 'approved' | 'rejected' | 'informationRequested'
  /** The tile's own on-screen label, so a guard failure can name what a reader sees. */
  readonly label: string
  readonly claims: (status: string) => boolean
}

/**
 * The four tiles, in the order the dashboard renders them.
 *
 * ⚠ **The two level tiles ask `pendingDecision()` rather than listing statuses.**
 * That is the one definition of "which decision is this bill waiting for" in this
 * app — the approval queue's membership and the lifecycle button gate both read
 * it — and a second copy here is exactly the S-011 shape that let `AWAITING_L2`
 * be computed and never counted (I9).
 */
export const BREAKDOWN_TILES: readonly BreakdownTile[] = [
  { key: 'awaitingL1', label: 'Awaiting level 1', claims: (status) => pendingDecision(status) === 'LEVEL_1' },
  { key: 'awaitingL2', label: 'Awaiting level 2', claims: (status) => pendingDecision(status) === 'LEVEL_2' },
  { key: 'approved', label: 'Approved', claims: (status) => status === 'APPROVED' },
  { key: 'rejected', label: 'Rejected', claims: (status) => status === 'REJECTED' },
  // ★★ WAVE 10 — THE FIFTH TILE, and the case this file's header predicted:
  // *"a SIXTH status added by a migration would land in `total` and in no tile"*.
  // It has its own tile rather than being folded into a level tile, because a
  // paused bill is NOT waiting for an officer — it is waiting for the HSP, and
  // adding it to "Awaiting level 1" would tell a manager they have work they do
  // not have. `pendingDecision()` returns `null` for it, so neither level tile
  // claims it and the partition holds.
  {
    key: 'informationRequested',
    label: 'Paused for information',
    claims: (status) => status === 'INFORMATION_REQUESTED',
  },
]

/** The six figures the dashboard's measure row renders, plus what it could not place. */
export interface StatusBreakdown {
  /** The endpoint's own `total` — the sum of every group it returned. */
  readonly total: number
  readonly awaitingL1: number
  readonly awaitingL2: number
  readonly approved: number
  readonly rejected: number
  /** ★★ WAVE 10 — paused pending information from the HSP; waiting on nobody here. */
  readonly informationRequested: number
  /**
   * Rows inside `total` that NO tile claims. Zero for every status this build
   * knows about; non-zero only when the database holds a status this build's
   * `BREAKDOWN_TILES` predates.
   */
  readonly unclassified: number
  /** Which statuses those rows carry, sorted, so the screen can name them. */
  readonly unclassifiedStatuses: readonly string[]
  /**
   * Do the five figures add up to `total`?
   *
   * `false` means the endpoint's `total` disagrees with the sum of its own
   * groups — a server-side inconsistency `bill-status-counts.test.ts` asserts
   * against SQL and this flag refuses to absorb anyway. A breakdown whose parts
   * do not sum to its own whole is the defect Task 2 removed; reporting it is
   * cheaper than trusting that it cannot recur.
   */
  readonly reconciled: boolean
}

/**
 * Classify one `GET /api/bills/status-counts` response into the dashboard's
 * five figures, and account for every row it counted.
 *
 * ⚠ **Nothing is dropped.** Every group lands in exactly one of the FIVE tiles
 * or in `unclassified`, so `awaitingL1 + awaitingL2 + approved + rejected +
 * informationRequested + unclassified` is the sum of the groups — and
 * `reconciled` says whether that equals the `total` the endpoint reported.
 */
export function breakdownOf(counts: {
  readonly total: number
  readonly byStatus: Readonly<Record<string, number>>
}): StatusBreakdown {
  const figures: Record<BreakdownTile['key'], number> = {
    awaitingL1: 0,
    awaitingL2: 0,
    approved: 0,
    rejected: 0,
    informationRequested: 0,
  }
  let unclassified = 0
  const unclassifiedStatuses: string[] = []

  for (const [status, n] of Object.entries(counts.byStatus)) {
    const tile = BREAKDOWN_TILES.find((t) => t.claims(status))
    if (tile === undefined) {
      unclassified += n
      unclassifiedStatuses.push(status)
      continue
    }
    figures[tile.key] += n
  }

  const placed =
    figures.awaitingL1 +
    figures.awaitingL2 +
    figures.approved +
    figures.rejected +
    figures.informationRequested +
    unclassified
  return {
    total: counts.total,
    ...figures,
    unclassified,
    unclassifiedStatuses: [...unclassifiedStatuses].sort(),
    reconciled: placed === counts.total,
  }
}
